import { useState, useEffect, useRef, lazy, Suspense, useEffectEvent, type ReactElement } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import {
  getJiraStatus,
  searchJiraByJql,
  startTestCaseUpdate,
  getTestCaseUpdateStatus,
  getTestCaseUpdateResults,
  startTestCaseRewrite,
  getTestCaseRewriteResults,
  exportTestCaseUpdate,
  type JiraConnectionInfo,
  type JiraSprintIssue,
  type TCUpdateResults,
  type TCRewriteResult,
  type TCRewrittenCase,
} from '../api'
import AppLayout from '../components/AppLayout'
import SprintImportModal from '../components/SprintImportModal'
import { friendlyError, errorMessage } from '../utils/friendlyError'

const TestCaseUpdateInstructions = lazy(() => import('./TestCaseUpdateInstructions'))

// ============================================================================
// Types
// ============================================================================

type Screen = 'input' | 'results'
type ResultsTab = 'updates' | 'new' | 'obsolete'

interface UploadedCsvInfo {
  fileName: string
  detectedFormat: 'testrail' | 'zephyr' | 'unknown'
  totalCases: number
  areas: string[]
}

// ============================================================================
// Helpers
// ============================================================================

/** Parse the first line of a CSV to detect the format (TestRail vs Zephyr) */
function detectCsvFormat(firstLine: string): 'testrail' | 'zephyr' | 'unknown' {
  const lower = firstLine.toLowerCase()
  if (lower.includes('"id"') || lower.includes(',id,') || lower.startsWith('id,') || lower.includes('section')) {
    return 'testrail'
  }
  if (
    lower.includes('"key"') || lower.includes(',key,') || lower.startsWith('key,') || lower.includes('folder') ||
    lower.includes('test case id') || lower.includes('hierarchy')
  ) {
    return 'zephyr'
  }
  return 'unknown'
}

/**
 * Parse CSV text into rows, respecting quoted cells that contain commas and
 * newlines. Mirrors the backend parseCSV so the upload preview matches what
 * the Lambda will actually see.
 */
function parseCsvRows(text: string): string[][] {
  const rows: string[][] = []
  let currentRow: string[] = []
  let currentField = ''
  let insideQuotes = false
  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    const nextChar = text[i + 1]
    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        currentField += '"'
        i++
      } else {
        insideQuotes = !insideQuotes
      }
    } else if (char === ',' && !insideQuotes) {
      currentRow.push(currentField)
      currentField = ''
    } else if ((char === '\n' || char === '\r') && !insideQuotes) {
      if (char === '\r' && nextChar === '\n') i++
      if (currentField || currentRow.length > 0) {
        currentRow.push(currentField)
        if (currentRow.some(f => f.trim())) rows.push(currentRow)
        currentRow = []
        currentField = ''
      }
    } else {
      currentField += char
    }
  }
  if (currentField || currentRow.length > 0) {
    currentRow.push(currentField)
    if (currentRow.some(f => f.trim())) rows.push(currentRow)
  }
  return rows
}

function findColIndex(headers: string[], names: string[]): number {
  const normalized = headers.map(h => h.toLowerCase().trim().replace(/[_\s]+/g, ''))
  for (const name of names) {
    const target = name.toLowerCase().trim().replace(/[_\s]+/g, '')
    const idx = normalized.indexOf(target)
    if (idx !== -1) return idx
  }
  return -1
}

/**
 * Count logical test cases in a CSV, applying the same grouping rules as the
 * backend: consecutive rows with no key (or a key repeating the previous row)
 * and no new folder are treated as step continuations of the previous case.
 */
function countCsvRows(text: string, format: 'testrail' | 'zephyr' | 'unknown'): number {
  const rows = parseCsvRows(text)
  if (rows.length < 2) return 0
  const headers = rows[0]
  const dataRows = rows.slice(1)

  if (format === 'testrail') {
    const idIdx = findColIndex(headers, ['ID'])
    if (idIdx === -1) return dataRows.filter(r => r.some(c => c.trim())).length
    let count = 0
    for (const r of dataRows) if ((r[idIdx] || '').trim()) count++
    return count
  }

  // Zephyr (covers both tree and regression-suite shapes)
  const keyIdx = findColIndex(headers, ['Key', 'Test case ID'])
  const summaryIdx = findColIndex(headers, ['Summary', 'Test case Name'])
  const folderIdx = findColIndex(headers, ['Folder', 'Hierarchy'])
  if (summaryIdx === -1) return dataRows.length

  let count = 0
  let previousId = ''
  let previousSummary = ''
  for (const row of dataRows) {
    const key = keyIdx !== -1 ? (row[keyIdx] || '').trim() : ''
    const summary = (row[summaryIdx] || '').trim()
    const rowFolder = folderIdx !== -1 ? (row[folderIdx] || '').trim() : ''
    const isContinuation =
      count > 0 &&
      !rowFolder &&
      (
        (!key && (!summary || summary === previousSummary))
        || (!!key && key === previousId)
      )
    if (isContinuation) continue
    if (!summary) continue
    count++
    previousId = key || `TC-${count}`
    previousSummary = summary
  }
  return count
}

/** Extract unique areas/sections from CSV using the quote-aware parser */
function extractAreas(text: string, format: 'testrail' | 'zephyr' | 'unknown'): string[] {
  const rows = parseCsvRows(text)
  if (rows.length < 2) return []
  const headers = rows[0]
  const areaIdx = format === 'testrail'
    ? findColIndex(headers, ['Section', 'Section Description'])
    : format === 'zephyr'
      ? findColIndex(headers, ['Folder', 'Component', 'Hierarchy'])
      : -1
  if (areaIdx < 0) return []
  const areas = new Set<string>()
  for (let i = 1; i < rows.length; i++) {
    const val = (rows[i][areaIdx] || '').trim()
    if (val) areas.add(val)
  }
  return Array.from(areas).sort()
}

/** JIRA issue status color helper */
function statusColor(color: string): string {
  switch (color) {
    case 'blue-gray': return 'bg-linear-text-muted/20 text-linear-text-muted'
    case 'blue': return 'bg-info/15 text-info'
    case 'green': return 'bg-success/15 text-success'
    case 'yellow': return 'bg-warning/15 text-warning'
    default: return 'bg-linear-text-muted/20 text-linear-text-muted'
  }
}

/**
 * Render an analysis error message, appending a Settings link when the
 * backend error mentions a Jira connection problem. Keeps the error banner
 * minimal for other error strings.
 */
function renderAnalysisError(message: string): ReactElement {
  const lower = message.toLowerCase()
  const isJiraIssue = lower.includes('jira connection') || lower.includes('jira tickets')
  if (!isJiraIssue) {
    return <>{message}</>
  }
  return (
    <>
      {message}{' '}
      <Link to="/settings" className="underline text-accent hover:text-accent-hover">
        Open Settings
      </Link>
    </>
  )
}

// ============================================================================
// Component
// ============================================================================

export default function TestCaseUpdate() {
  const navigate = useNavigate()
  const location = useLocation()

  // --------------------------------------------------------------------------
  // Screen state
  // --------------------------------------------------------------------------
  const [currentScreen, setCurrentScreen] = useState<Screen>('input')

  // --------------------------------------------------------------------------
  // CSV Upload state
  // --------------------------------------------------------------------------
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvBase64, setCsvBase64] = useState<string>('')
  const [uploadedInfo, setUploadedInfo] = useState<UploadedCsvInfo | null>(null)
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Single-flight guard. setLoading(true) inside handleStartAnalysis updates
  // state asynchronously, so a rapid second click can fire startTestCaseUpdate
  // before the disabled prop catches up — that path was creating orphaned
  // pending jobs in DynamoDB. The ref blocks the second click synchronously.
  const submitInFlightRef = useRef(false)

  // --------------------------------------------------------------------------
  // Ticket state
  // --------------------------------------------------------------------------
  const [tickets, setTickets] = useState<string[]>([])
  const [ticketInput, setTicketInput] = useState('')

  // --------------------------------------------------------------------------
  // Jira connection state
  // --------------------------------------------------------------------------
  const [jiraStatus, setJiraStatus] = useState<JiraConnectionInfo | null>(null)

  // --------------------------------------------------------------------------
  // Sprint import modal state
  // --------------------------------------------------------------------------
  const [sprintModalOpen, setSprintModalOpen] = useState(false)

  // --------------------------------------------------------------------------
  // JQL import state
  // --------------------------------------------------------------------------
  const [jqlExpanded, setJqlExpanded] = useState(false)
  const [jqlQuery, setJqlQuery] = useState('')
  const [jqlResults, setJqlResults] = useState<JiraSprintIssue[]>([])
  const [jqlTotal, setJqlTotal] = useState(0)
  const [jqlHasMore, setJqlHasMore] = useState(false)
  const [jqlLoading, setJqlLoading] = useState(false)
  const [jqlError, setJqlError] = useState<string | null>(null)
  const [jqlSelectedKeys, setJqlSelectedKeys] = useState<Set<string>>(new Set())

  // --------------------------------------------------------------------------
  // Analysis state
  // --------------------------------------------------------------------------
  const [analysisJobId, setAnalysisJobId] = useState<string | null>(null)
  const [analysisStatus, setAnalysisStatus] = useState<'idle' | 'processing' | 'completed' | 'failed'>('idle')
  const [analysisProgress, setAnalysisProgress] = useState(0)
  const [analysisMessage, setAnalysisMessage] = useState('')
  const [results, setResults] = useState<TCUpdateResults | null>(null)

  // --------------------------------------------------------------------------
  // Results UI state
  // --------------------------------------------------------------------------
  const [activeTab, setActiveTab] = useState<ResultsTab>('updates')
  const [expandedCases, setExpandedCases] = useState<Set<string>>(new Set())

  // --------------------------------------------------------------------------
  // Rewrite state
  // --------------------------------------------------------------------------
  const [rewriteStatus, setRewriteStatus] = useState<'idle' | 'processing' | 'completed' | 'failed'>('idle')
  const [rewriteResults, setRewriteResults] = useState<TCRewriteResult | null>(null)

  // --------------------------------------------------------------------------
  // Export state
  // --------------------------------------------------------------------------
  const [exportOpen, setExportOpen] = useState(false)

  // --------------------------------------------------------------------------
  // General state
  // --------------------------------------------------------------------------
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [initializing, setInitializing] = useState(true)

  // ==========================================================================
  // Effects
  // ==========================================================================

  // Fetch JIRA connection status on mount
  useEffect(() => {
    getJiraStatus()
      .then(setJiraStatus)
      .catch(() => setJiraStatus(null))
    setInitializing(false)
  }, [])

  // URL routing
  const onRouteChange = useEffectEvent(() => {
    const path = location.pathname

    // /test-case-updater/instructions
    if (path.includes('/instructions')) {
      return
    }

    // /test-case-updater/{jobId}
    const jobMatch = path.match(/^\/test-case-updater\/([a-f0-9-]+)$/)
    if (jobMatch) {
      const jobId = jobMatch[1]
      setCurrentScreen('results')
      loadResults(jobId)
      return
    }

    // /test-case-updater/ (default input)
    if (currentScreen === 'results' && !results) {
      setCurrentScreen('input')
    }
  })
  useEffect(() => {
    onRouteChange()
  }, [location.pathname])

  // Poll analysis status
  const analysisPollCount = useRef(0)
  // navigate changes identity on every route change under BrowserRouter;
  // reading it through an effect event keeps it from restarting the poll.
  const goToResults = useEffectEvent((path: string) => navigate(path))
  useEffect(() => {
    if (!analysisJobId || analysisStatus !== 'processing') return
    analysisPollCount.current = 0

    const interval = setInterval(async () => {
      analysisPollCount.current += 1
      if (analysisPollCount.current > 300) {
        setAnalysisStatus('failed')
        setError('Analysis timed out after 10 minutes. Please try again.')
        clearInterval(interval)
        return
      }
      try {
        const status = await getTestCaseUpdateStatus(analysisJobId)
        setAnalysisProgress(status.progress || 0)
        setAnalysisMessage(status.statusMessage || '')

        if (status.status === 'completed') {
          setAnalysisStatus('completed')
          clearInterval(interval)
          try {
            const resultData = await getTestCaseUpdateResults(analysisJobId)
            setResults(resultData)
            setCurrentScreen('results')
            goToResults(`/test-case-updater/${analysisJobId}`)
          } catch (resultsErr) {
            setAnalysisStatus('failed')
            setError(
              resultsErr instanceof Error
                ? resultsErr.message
                : 'Analysis finished but results could not be loaded. Please try again.'
            )
          }
        } else if (status.status === 'failed') {
          setAnalysisStatus('failed')
          setError(status.error || 'Analysis failed')
          clearInterval(interval)
        }
      } catch (err) {
        console.error('Error polling analysis status:', err)
      }
    }, 2000)

    return () => clearInterval(interval)
  }, [analysisJobId, analysisStatus])

  // Poll rewrite status
  const rewritePollCount = useRef(0)
  useEffect(() => {
    if (!analysisJobId || rewriteStatus !== 'processing') return
    rewritePollCount.current = 0

    const interval = setInterval(async () => {
      rewritePollCount.current += 1
      if (rewritePollCount.current > 300) {
        setRewriteStatus('failed')
        setError('Rewrite timed out after 10 minutes. Please try again.')
        clearInterval(interval)
        return
      }
      try {
        const status = await getTestCaseUpdateStatus(analysisJobId)

        if (status.status === 'failed') {
          setRewriteStatus('failed')
          setError(status.error || 'Rewrite failed')
          clearInterval(interval)
          return
        }

        if (status.status === 'completed' && status.hasRewrite) {
          try {
            const rewriteData = await getTestCaseRewriteResults(analysisJobId)
            if (rewriteData && rewriteData.rewrittenCases && rewriteData.rewrittenCases.length > 0) {
              setRewriteStatus('completed')
              setRewriteResults(rewriteData)
              clearInterval(interval)
            }
          } catch {
            // Rewrite results not ready yet despite status, keep polling
          }
        }
      } catch (err) {
        console.error('Error polling rewrite status:', err)
      }
    }, 2000)

    return () => clearInterval(interval)
  }, [analysisJobId, rewriteStatus])

  // ==========================================================================
  // Data loading
  // ==========================================================================

  async function loadResults(jobId: string) {
    setLoading(true)
    setError(null)
    try {
      const resultData = await getTestCaseUpdateResults(jobId)
      setResults(resultData)
      setAnalysisJobId(jobId)
      setAnalysisStatus('completed')

      // Try to load rewrite results if they exist
      try {
        const rewriteData = await getTestCaseRewriteResults(jobId)
        if (rewriteData && rewriteData.rewrittenCases && rewriteData.rewrittenCases.length > 0) {
          setRewriteResults(rewriteData)
          setRewriteStatus('completed')
        }
      } catch {
        // No rewrite results yet, that's fine
      }
    } catch (err) {
      setError(friendlyError(errorMessage(err) || 'Failed to load results', 'load the analysis results'))
      setCurrentScreen('input')
    } finally {
      setLoading(false)
    }
  }

  // ==========================================================================
  // CSV upload handlers
  // ==========================================================================

  function handleCsvFile(file: File) {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please upload a CSV file')
      return
    }
    setError(null)
    setCsvFile(file)

    const reader = new FileReader()
    reader.onload = () => {
      const text = reader.result as string

      // Convert to base64
      const base64 = btoa(unescape(encodeURIComponent(text)))
      setCsvBase64(base64)

      // Detect format from first line
      const firstLine = text.split('\n')[0] || ''
      const format = detectCsvFormat(firstLine)
      const totalCases = countCsvRows(text, format)
      const areas = extractAreas(text, format)

      setUploadedInfo({
        fileName: file.name,
        detectedFormat: format,
        totalCases,
        areas,
      })
    }
    reader.onerror = () => {
      setError('Failed to read the file')
    }
    reader.readAsText(file)
  }

  function clearCsv() {
    setCsvFile(null)
    setCsvBase64('')
    setUploadedInfo(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // ==========================================================================
  // Ticket handlers
  // ==========================================================================

  function addTicket(ticket: string) {
    const trimmed = ticket.trim().toUpperCase()
    if (trimmed && /^[A-Z]+-\d+$/.test(trimmed) && !tickets.includes(trimmed)) {
      setTickets([...tickets, trimmed])
      setTicketInput('')
    } else if (trimmed && !/^[A-Z]+-\d+$/.test(trimmed)) {
      setError('Invalid ticket format. Use format like BAT-100')
      setTimeout(() => setError(null), 3000)
    }
  }

  function removeTicket(ticket: string) {
    setTickets(tickets.filter(t => t !== ticket))
  }

  function handleTicketKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (ticketInput.trim()) {
        addTicket(ticketInput)
      } else if (tickets.length > 0 && csvFile) {
        handleStartAnalysis()
      }
    } else if (e.key === ',' || e.key === ' ') {
      e.preventDefault()
      if (ticketInput.trim()) {
        addTicket(ticketInput)
      }
    } else if (e.key === 'Backspace' && ticketInput === '' && tickets.length > 0) {
      setTickets(tickets.slice(0, -1))
    }
  }

  // ==========================================================================
  // JQL handlers (lifted from RiskPrioritization)
  // ==========================================================================

  async function handleJqlPreview() {
    setJqlLoading(true)
    setJqlError(null)
    setJqlSelectedKeys(new Set())
    try {
      const result = await searchJiraByJql(jqlQuery.trim())
      if (result.error) {
        setJqlError(result.error)
        setJqlResults([])
        setJqlTotal(0)
        setJqlHasMore(false)
      } else {
        setJqlResults(result.issues)
        setJqlTotal(result.total)
        setJqlHasMore(result.hasMore)
        setJqlSelectedKeys(new Set(result.issues.map(i => i.key)))
      }
    } catch {
      setJqlError('Failed to execute JQL query')
    } finally {
      setJqlLoading(false)
    }
  }

  async function handleJqlLoadMore() {
    setJqlLoading(true)
    try {
      const result = await searchJiraByJql(jqlQuery.trim(), jqlResults.length)
      if (!result.error) {
        setJqlResults(prev => [...prev, ...result.issues])
        setJqlHasMore(result.hasMore)
        setJqlSelectedKeys(prev => {
          const next = new Set(prev)
          result.issues.forEach(i => next.add(i.key))
          return next
        })
      }
    } finally {
      setJqlLoading(false)
    }
  }

  function toggleJqlKey(key: string) {
    setJqlSelectedKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function toggleJqlSelectAll() {
    if (jqlSelectedKeys.size === jqlResults.length) {
      setJqlSelectedKeys(new Set())
    } else {
      setJqlSelectedKeys(new Set(jqlResults.map(i => i.key)))
    }
  }

  function handleJqlImport() {
    const newTickets = [...new Set([...tickets, ...jqlSelectedKeys])]
    setTickets(newTickets)
    setJqlResults([])
    setJqlQuery('')
    setJqlExpanded(false)
  }

  // ==========================================================================
  // Analysis handler
  // ==========================================================================

  async function handleStartAnalysis() {
    if (submitInFlightRef.current) return
    if (!csvFile || !csvBase64 || tickets.length === 0) {
      setError('Please upload a CSV file and add at least one ticket')
      return
    }

    submitInFlightRef.current = true
    setLoading(true)
    setError(null)
    setAnalysisStatus('processing')
    setAnalysisProgress(0)
    setAnalysisMessage('Starting analysis...')
    setRewriteStatus('idle')
    setRewriteResults(null)

    try {
      const result = await startTestCaseUpdate({
        csvBase64,
        fileName: csvFile.name,
        jiraTicketKeys: tickets,
        manualTickets: [],
      })
      setAnalysisJobId(result.updateJobId)
    } catch (err) {
      setError(friendlyError(errorMessage(err) || 'Failed to start analysis', 'start the analysis'))
      setAnalysisStatus('failed')
    } finally {
      setLoading(false)
      submitInFlightRef.current = false
    }
  }

  // ==========================================================================
  // Rewrite handler
  // ==========================================================================

  async function handleStartRewrite() {
    if (!analysisJobId) return

    setRewriteStatus('processing')
    setRewriteResults(null)
    setError(null)

    try {
      await startTestCaseRewrite(analysisJobId)
    } catch (err) {
      setError(friendlyError(errorMessage(err) || 'Failed to start rewrite', 'start the rewrite'))
      setRewriteStatus('failed')
    }
  }

  // ==========================================================================
  // Export handler
  // ==========================================================================

  async function handleExport(format: 'csv' | 'testrail' | 'zephyr') {
    if (!analysisJobId) return
    setExportOpen(false)
    try {
      const csvContent = await exportTestCaseUpdate({
        updateJobId: analysisJobId,
        exportType: rewriteResults ? 'rewrite' : 'analysis',
        format,
      })
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `test-case-updater-${format}-${analysisJobId}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(friendlyError(errorMessage(err) || 'Failed to export', 'export the results'))
    }
  }

  // ==========================================================================
  // UI helpers
  // ==========================================================================

  function toggleCase(id: string) {
    setExpandedCases(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function handleNewAnalysis() {
    setCurrentScreen('input')
    setCsvFile(null)
    setCsvBase64('')
    setUploadedInfo(null)
    setTickets([])
    setTicketInput('')
    setAnalysisJobId(null)
    setAnalysisStatus('idle')
    setAnalysisProgress(0)
    setAnalysisMessage('')
    setResults(null)
    setActiveTab('updates')
    setExpandedCases(new Set())
    setRewriteStatus('idle')
    setRewriteResults(null)
    setExportOpen(false)
    setError(null)
    navigate('/test-case-updater/')
  }

  /** Look up the rewritten version of a test case by its original ID */
  function getRewrittenCase(existingTestCaseId: string): TCRewrittenCase | undefined {
    if (!rewriteResults) return undefined
    return rewriteResults.rewrittenCases.find(r => r.existingTestCaseId === existingTestCaseId)
  }

  // ==========================================================================
  // Render: Initializing
  // ==========================================================================
  if (initializing) {
    return (
      <div className="min-h-screen bg-linear-bg flex items-center justify-center">
        <div className="text-center">
          <div className="spinner spinner-lg mx-auto mb-4"></div>
          <div className="text-linear-text-muted">Loading...</div>
        </div>
      </div>
    )
  }

  // ==========================================================================
  // Render: Instructions (lazy loaded)
  // ==========================================================================
  if (location.pathname.includes('/instructions')) {
    return (
      <Suspense fallback={
        <div className="min-h-screen bg-linear-bg flex items-center justify-center">
          <div className="text-center">
            <div className="spinner spinner-lg mx-auto mb-4"></div>
            <div className="text-linear-text-muted">Loading instructions...</div>
          </div>
        </div>
      }>
        <TestCaseUpdateInstructions
          onBack={() => {
            navigate('/test-case-updater/')
          }}
        />
      </Suspense>
    )
  }

  // ==========================================================================
  // Render: Loading results
  // ==========================================================================
  if (loading && currentScreen === 'results') {
    return (
      <AppLayout
        breadcrumbs={[
          { label: 'Test Case Updater', href: '/test-case-updater/' },
          { label: 'Loading...' },
        ]}
      >
        <div className="max-w-[1400px] mx-auto flex items-center justify-center py-24">
          <div className="text-center">
            <div className="spinner spinner-lg mx-auto mb-4"></div>
            <div className="text-linear-text-muted">Loading analysis results...</div>
          </div>
        </div>
      </AppLayout>
    )
  }

  // ==========================================================================
  // Render: Results Screen
  // ==========================================================================
  if (currentScreen === 'results' && results) {
    const { summary, casesToUpdate, newTestCases, obsoleteTestCases } = results

    return (
      <AppLayout
        breadcrumbs={[
          { label: 'Test Case Updater', href: '/test-case-updater/' },
          { label: 'Results' },
        ]}
        headerActions={
          <button
            onClick={handleNewAnalysis}
            className="btn btn-primary text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Analysis
          </button>
        }
      >
        <div className="max-w-[1400px] mx-auto">
          {error && (
            <div className="toast toast-error mb-4">
              <svg className="w-5 h-5 text-error flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-linear-text">{renderAnalysisError(error)}</span>
            </div>
          )}

          {(() => {
            const allEmpty =
              casesToUpdate.length === 0 &&
              newTestCases.length === 0 &&
              obsoleteTestCases.length === 0
            if (!allEmpty) return null
            const requested = summary.jiraTicketsRequested ?? 0
            const resolved = summary.jiraContextsResolved ?? 0
            // Only steer the user to reconnect when Jira keys were submitted
            // but none came back — otherwise this fires for legitimate
            // "AI found nothing" runs and creates false alarms.
            // For older jobs without these fields, fall back to the legacy
            // catch-all so Lisa's df68d9a5 still surfaces a banner.
            const isLegacyEmpty =
              summary.jiraTicketsRequested === undefined &&
              summary.jiraContextsResolved === undefined
            const jiraLikelyFailed =
              isLegacyEmpty || (requested > 0 && resolved === 0)
            if (!jiraLikelyFailed) return null
            return (
              <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 mb-6 flex items-start gap-3">
                <svg className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="text-sm text-warning">
                  <p className="font-medium mb-1">No updates suggested for this run.</p>
                  <p>
                    If this is unexpected, your Jira connection may not have returned ticket
                    details for this analysis. Try reconnecting in{' '}
                    <Link to="/settings" className="underline font-medium">Settings → Integrations</Link>
                    {' '}and re-run the analysis.
                  </p>
                </div>
              </div>
            )
          })()}

          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-linear-text-muted/15 flex items-center justify-center">
                  <svg className="w-5 h-5 text-linear-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div>
                  <div className="text-2xl font-bold text-linear-text">{summary.totalAnalyzed}</div>
                  <div className="text-sm text-linear-text-muted">Total Analyzed</div>
                </div>
              </div>
            </div>
            <div className="card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-warning/15 flex items-center justify-center">
                  <svg className="w-5 h-5 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <div>
                  <div className="text-2xl font-bold text-warning">{summary.toUpdate}</div>
                  <div className="text-sm text-linear-text-muted">Updates Needed</div>
                </div>
              </div>
            </div>
            <div className="card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-success/15 flex items-center justify-center">
                  <svg className="w-5 h-5 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <div>
                  <div className="text-2xl font-bold text-success">{summary.newGaps}</div>
                  <div className="text-sm text-linear-text-muted">New Cases</div>
                </div>
              </div>
            </div>
            <div className="card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-error/15 flex items-center justify-center">
                  <svg className="w-5 h-5 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <div className="text-2xl font-bold text-error">{summary.obsolete}</div>
                  <div className="text-sm text-linear-text-muted">Obsolete</div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions row: Export + Rewrite */}
          {(() => {
            const exportEmpty =
              casesToUpdate.length === 0 &&
              newTestCases.length === 0 &&
              obsoleteTestCases.length === 0
            return (
          <div className="flex items-center justify-between mb-6">
            {/* Export Dropdown */}
            <div className="relative">
              <button
                onClick={() => !exportEmpty && setExportOpen(!exportOpen)}
                disabled={exportEmpty}
                title={exportEmpty ? 'Nothing to export from this run' : undefined}
                className="btn btn-outline text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export Results
                <svg className={`w-4 h-4 transition-transform ${exportOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {exportOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setExportOpen(false)}
                  />
                  <div className="dropdown absolute left-0 mt-2 w-48 z-20">
                    <button
                      onClick={() => handleExport('csv')}
                      className="dropdown-item w-full text-left"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      CSV
                    </button>
                    <button
                      onClick={() => handleExport('testrail')}
                      className="dropdown-item w-full text-left"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      TestRail CSV
                    </button>
                    <button
                      onClick={() => handleExport('zephyr')}
                      className="dropdown-item w-full text-left"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Zephyr CSV
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Rewrite All button */}
            {casesToUpdate.length > 0 && rewriteStatus === 'idle' && (
              <button
                onClick={handleStartRewrite}
                className="btn btn-primary text-sm flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Rewrite All Updates
              </button>
            )}
            {rewriteStatus === 'processing' && (
              <div className="flex items-center gap-2 text-sm text-linear-text-muted">
                <div className="spinner spinner-sm"></div>
                Rewriting test cases...
              </div>
            )}
            {rewriteStatus === 'completed' && (
              <div className="flex items-center gap-2 text-sm text-success">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Rewrite complete
              </div>
            )}
          </div>
            )
          })()}

          {/* Tab Bar */}
          <div className="flex border-b border-linear-border mb-6">
            <button
              onClick={() => setActiveTab('updates')}
              className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                activeTab === 'updates'
                  ? 'text-linear-text'
                  : 'text-linear-text-muted hover:text-linear-text'
              }`}
            >
              Updates Needed
              <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-warning/15 text-warning">{casesToUpdate.length}</span>
              {activeTab === 'updates' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-linear-accent" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('new')}
              className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                activeTab === 'new'
                  ? 'text-linear-text'
                  : 'text-linear-text-muted hover:text-linear-text'
              }`}
            >
              New Cases
              <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-success/15 text-success">{newTestCases.length}</span>
              {activeTab === 'new' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-linear-accent" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('obsolete')}
              className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                activeTab === 'obsolete'
                  ? 'text-linear-text'
                  : 'text-linear-text-muted hover:text-linear-text'
              }`}
            >
              Obsolete Cases
              <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-error/15 text-error">{obsoleteTestCases.length}</span>
              {activeTab === 'obsolete' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-linear-accent" />
              )}
            </button>
          </div>

          {/* Tab Content */}
          <div className="space-y-3">
            {/* ============================================================ */}
            {/* Tab 1: Updates Needed                                        */}
            {/* ============================================================ */}
            {activeTab === 'updates' && (
              <>
                {casesToUpdate.length === 0 ? (
                  <div className="card p-8 text-center">
                    <svg className="w-12 h-12 text-linear-text-muted mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-linear-text-muted">All test cases are up to date. No updates needed.</p>
                  </div>
                ) : (
                  casesToUpdate.map((rec) => {
                    const caseKey = `update-${rec.existingTestCaseId}`
                    const isExpanded = expandedCases.has(caseKey)
                    const rewritten = getRewrittenCase(rec.existingTestCaseId)

                    return (
                      <div key={caseKey} className="card overflow-hidden">
                        {/* Collapsed header */}
                        <div
                          role="button"
                          tabIndex={0}
                          aria-expanded={isExpanded}
                          className="flex items-center justify-between p-4 cursor-pointer hover:bg-linear-bg-hover transition-colors group"
                          onClick={() => toggleCase(caseKey)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCase(caseKey) } }}
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            {/* Impact badge */}
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0 ${
                              rec.impactLevel === 'high'
                                ? 'bg-error/15 text-error'
                                : rec.impactLevel === 'medium'
                                  ? 'bg-warning/15 text-warning'
                                  : 'bg-info/15 text-info'
                            }`}>
                              {rec.impactLevel.charAt(0).toUpperCase() + rec.impactLevel.slice(1)}
                            </span>

                            {/* ID + Title */}
                            <span className="text-xs font-mono text-linear-text-muted flex-shrink-0">{rec.existingTestCaseId}</span>
                            <span className={`text-sm font-medium text-linear-text ${!isExpanded ? 'truncate' : ''}`}>{rec.existingTitle}</span>
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                            {/* Area tag */}
                            {rec.area && (
                              <span className="text-xs px-2 py-0.5 rounded bg-linear-bg-card border border-linear-border text-linear-text-secondary">{rec.area}</span>
                            )}
                            {/* Affected tickets */}
                            {rec.affectedTickets.map(t => (
                              <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-linear-accent/20 text-linear-accent font-medium">{t}</span>
                            ))}
                            <svg
                              className={`w-5 h-5 text-linear-text-muted group-hover:text-linear-text transition-all ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>

                        {/* Expanded content */}
                        {isExpanded && (
                          <div className="border-t border-linear-border p-4 space-y-4 animate-fade-in">
                            {/* Change type */}
                            <div className="text-xs font-medium text-linear-text-muted uppercase tracking-wider">Change Type</div>
                            <p className="text-sm text-linear-text-secondary -mt-2">{rec.changeType}</p>

                            {/* Current steps (dimmed) */}
                            {rec.suggestedChanges.steps && rec.suggestedChanges.steps.length > 0 && (
                              <div>
                                <div className="text-xs font-medium text-linear-text-muted uppercase tracking-wider mb-2">Current Steps</div>
                                <div className="bg-linear-bg/60 border border-linear-border rounded-lg p-3 opacity-60">
                                  {/* If we had original steps, we'd show them here. For now show a note. */}
                                  <p className="text-xs text-linear-text-muted italic">Original steps from the uploaded CSV</p>
                                </div>
                              </div>
                            )}

                            {/* AI Recommendation */}
                            <div>
                              <div className="text-xs font-medium text-linear-text-muted uppercase tracking-wider mb-2">AI Recommendation</div>
                              <div className="border-l-2 border-info bg-info/5 rounded-r-lg p-4">
                                <p className="text-sm text-linear-text-secondary whitespace-pre-wrap">{rec.recommendation}</p>

                                {/* Suggested changes details */}
                                {rec.suggestedChanges.title && (
                                  <div className="mt-3 pt-3 border-t border-info/20">
                                    <span className="text-xs font-medium text-info">Suggested Title:</span>
                                    <p className="text-sm text-linear-text mt-1">{rec.suggestedChanges.title}</p>
                                  </div>
                                )}

                                {rec.suggestedChanges.preconditions && (
                                  <div className="mt-3 pt-3 border-t border-info/20">
                                    <span className="text-xs font-medium text-info">Suggested Preconditions:</span>
                                    <p className="text-sm text-linear-text-secondary mt-1 whitespace-pre-wrap">{rec.suggestedChanges.preconditions}</p>
                                  </div>
                                )}

                                {rec.suggestedChanges.steps && rec.suggestedChanges.steps.length > 0 && (
                                  <div className="mt-3 pt-3 border-t border-info/20">
                                    <span className="text-xs font-medium text-info">Suggested Steps:</span>
                                    <div className="mt-2 overflow-hidden rounded-md border border-linear-border">
                                      <table className="w-full text-sm text-left">
                                        <thead className="bg-linear-bg-card text-linear-text-muted text-xs uppercase font-semibold">
                                          <tr>
                                            <th className="px-3 py-2 w-10 text-center">#</th>
                                            <th className="px-3 py-2">Action</th>
                                            <th className="px-3 py-2">Test Data</th>
                                            <th className="px-3 py-2">Expected Result</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-linear-border">
                                          {rec.suggestedChanges.steps.map(step => (
                                            <tr key={step.step_number} className="bg-linear-bg-sidebar">
                                              <td className="px-3 py-2 text-center text-linear-text-muted">{step.step_number}</td>
                                              <td className="px-3 py-2 text-linear-text-secondary">{step.action}</td>
                                              <td className="px-3 py-2 text-linear-text-muted">{step.test_data || '-'}</td>
                                              <td className="px-3 py-2 text-linear-text-secondary">{step.expected_result}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                                {rec.suggestedChanges.expectedResult && (
                                  <div className="mt-3 pt-3 border-t border-info/20">
                                    <span className="text-xs font-medium text-info">Expected Result Change:</span>
                                    <p className="text-sm text-linear-text-secondary mt-1 whitespace-pre-wrap">{rec.suggestedChanges.expectedResult}</p>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Rewritten version (if available) */}
                            {rewritten && (
                              <div>
                                <div className="text-xs font-medium text-success uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  Rewritten Test Case
                                </div>
                                <div className="border-l-2 border-success bg-success/5 rounded-r-lg p-4">
                                  <p className="text-sm font-medium text-linear-text mb-3">{rewritten.title}</p>

                                  {/* Preconditions */}
                                  {rewritten.preconditions.length > 0 && (
                                    <div className="mb-3">
                                      <span className="text-xs font-medium text-success">Preconditions:</span>
                                      <ul className="list-disc list-inside text-sm text-linear-text-secondary mt-1 space-y-0.5">
                                        {rewritten.preconditions.map((pre, idx) => (
                                          <li key={idx}>{pre}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {/* Steps table */}
                                  {rewritten.steps.length > 0 && (
                                    <div className="overflow-hidden rounded-md border border-linear-border">
                                      <table className="w-full text-sm text-left">
                                        <thead className="bg-linear-bg-card text-linear-text-muted text-xs uppercase font-semibold">
                                          <tr>
                                            <th className="px-3 py-2 w-10 text-center">#</th>
                                            <th className="px-3 py-2">Action</th>
                                            <th className="px-3 py-2">Test Data</th>
                                            <th className="px-3 py-2">Expected Result</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-linear-border">
                                          {rewritten.steps.map(step => (
                                            <tr key={step.step_number} className="bg-linear-bg-sidebar">
                                              <td className="px-3 py-2 text-center text-linear-text-muted">{step.step_number}</td>
                                              <td className="px-3 py-2 text-linear-text-secondary">{step.action}</td>
                                              <td className="px-3 py-2 text-linear-text-muted">{step.test_data || '-'}</td>
                                              <td className="px-3 py-2 text-linear-text-secondary">{step.expected_result}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}

                                  {/* Tags */}
                                  {rewritten.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-3">
                                      {rewritten.tags.map(tag => (
                                        <span key={tag} className="px-2 py-0.5 rounded text-xs bg-linear-bg-card border border-linear-border text-linear-text-secondary">
                                          #{tag.replace(/_/g, ' ')}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })
                )}
              </>
            )}

            {/* ============================================================ */}
            {/* Tab 2: New Cases                                             */}
            {/* ============================================================ */}
            {activeTab === 'new' && (
              <>
                {newTestCases.length === 0 ? (
                  <div className="card p-8 text-center">
                    <svg className="w-12 h-12 text-linear-text-muted mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-linear-text-muted">No coverage gaps found. Your test suite covers the sprint changes.</p>
                  </div>
                ) : (
                  newTestCases.map((tc, index) => {
                    const caseKey = `new-${index}-${tc.title.slice(0, 20)}`
                    const isExpanded = expandedCases.has(caseKey)
                    const typeBadgeMap: Record<string, { label: string; classes: string }> = {
                      happy_path: { label: 'Happy Path', classes: 'bg-success/15 text-success border border-success/30' },
                      sad_path: { label: 'Sad Path', classes: 'bg-error/15 text-error border border-error/30' },
                      edge_case: { label: 'Edge Case', classes: 'bg-warning/15 text-warning border border-warning/30' },
                    }
                    const priorityBadgeMap: Record<string, { label: string; classes: string }> = {
                      high: { label: 'High', classes: 'text-error bg-error/15 border border-error/30' },
                      medium: { label: 'Medium', classes: 'text-info bg-info/15 border border-info/30' },
                      low: { label: 'Low', classes: 'text-linear-text-muted bg-linear-text-muted/15 border border-linear-text-muted/30' },
                    }
                    const borderColorMap: Record<string, string> = {
                      happy_path: 'border-l-success',
                      sad_path: 'border-l-error',
                      edge_case: 'border-l-warning',
                    }
                    const typeBadge = typeBadgeMap[tc.type] || typeBadgeMap.happy_path
                    const priorityBadge = priorityBadgeMap[tc.priority] || priorityBadgeMap.medium
                    const borderColor = borderColorMap[tc.type] || 'border-l-green-500'

                    return (
                      <div key={caseKey} className={`bg-linear-bg-sidebar border-y border-r border-linear-border rounded-md overflow-hidden shadow-sm border-l-4 ${borderColor}`}>
                        {/* Header */}
                        <div
                          role="button"
                          tabIndex={0}
                          aria-expanded={isExpanded}
                          className="flex items-center justify-between p-4 cursor-pointer hover:bg-linear-bg-hover transition-colors group"
                          onClick={() => toggleCase(caseKey)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCase(caseKey) } }}
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="w-6 h-6 flex items-center justify-center rounded bg-linear-bg border border-linear-border text-xs font-mono text-linear-text-muted flex-shrink-0">
                              {String(index + 1).padStart(2, '0')}
                            </div>
                            <div className="flex flex-col min-w-0">
                              <h3 className={`text-sm font-medium text-linear-text ${!isExpanded ? 'truncate' : ''}`} title={tc.title}>
                                {tc.title}
                              </h3>
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeBadge.classes}`}>
                                  {typeBadge.label}
                                </span>
                                <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${priorityBadge.classes}`}>
                                  {priorityBadge.label}
                                </span>
                                {tc.area && (
                                  <span className="text-xs px-2 py-0.5 rounded bg-linear-bg-card border border-linear-border text-linear-text-secondary">{tc.area}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                            {tc.relatedTickets.map(t => (
                              <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-linear-accent/20 text-linear-accent font-medium">{t}</span>
                            ))}
                            <svg
                              className={`w-5 h-5 text-linear-text-muted group-hover:text-linear-text transition-all ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>

                        {/* Expanded content */}
                        {isExpanded && (
                          <div className="border-t border-linear-border bg-linear-bg-card/30 p-4 animate-fade-in">
                            {/* Rationale */}
                            <div className="mb-4">
                              <h4 className="text-xs font-semibold text-linear-text-muted uppercase tracking-wider mb-2">Rationale</h4>
                              <p className="text-sm text-linear-text-secondary">{tc.rationale}</p>
                            </div>

                            {/* Preconditions */}
                            {tc.preconditions.length > 0 && (
                              <div className="mb-4">
                                <h4 className="text-xs font-semibold text-linear-text-muted uppercase tracking-wider mb-2">Preconditions</h4>
                                <ul className="list-disc list-inside text-sm text-linear-text-secondary space-y-1 ml-1">
                                  {tc.preconditions.map((pre, idx) => (
                                    <li key={idx}>{pre}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Steps table */}
                            {tc.steps.length > 0 && (
                              <div>
                                <h4 className="text-xs font-semibold text-linear-text-muted uppercase tracking-wider mb-2">Test Steps</h4>
                                <div className="overflow-hidden rounded-md border border-linear-border">
                                  <table className="w-full text-sm text-left">
                                    <thead className="bg-linear-bg-card text-linear-text-muted text-xs uppercase font-semibold">
                                      <tr>
                                        <th className="px-4 py-2 w-12 text-center">#</th>
                                        <th className="px-4 py-2 w-1/3">Action</th>
                                        <th className="px-4 py-2 w-1/4">Test Data</th>
                                        <th className="px-4 py-2">Expected Result</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-linear-border">
                                      {tc.steps.map(step => (
                                        <tr key={step.step_number} className="bg-linear-bg-sidebar">
                                          <td className="px-4 py-2 text-center text-linear-text-muted">{step.step_number}</td>
                                          <td className="px-4 py-2 text-linear-text-secondary">{step.action}</td>
                                          <td className="px-4 py-2 text-linear-text-muted">{step.test_data || '-'}</td>
                                          <td className="px-4 py-2 text-linear-text-secondary">{step.expected_result}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })
                )}
              </>
            )}

            {/* ============================================================ */}
            {/* Tab 3: Obsolete Cases                                        */}
            {/* ============================================================ */}
            {activeTab === 'obsolete' && (
              <>
                {obsoleteTestCases.length === 0 ? (
                  <div className="card p-8 text-center">
                    <svg className="w-12 h-12 text-linear-text-muted mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-linear-text-muted">No obsolete test cases found.</p>
                  </div>
                ) : (
                  obsoleteTestCases.map((tc, index) => {
                    const confidenceBadge: Record<string, { label: string; classes: string }> = {
                      high: { label: 'High', classes: 'bg-error/15 text-error' },
                      medium: { label: 'Medium', classes: 'bg-warning/15 text-warning' },
                      low: { label: 'Low', classes: 'bg-info/15 text-info' },
                    }
                    const badge = confidenceBadge[tc.confidence] || confidenceBadge.medium

                    return (
                      <div key={`obsolete-${tc.existingTestCaseId}-${index}`} className="card p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-1">
                              <span className="text-xs font-mono text-linear-text-muted">{tc.existingTestCaseId}</span>
                              <span className="text-sm font-medium text-linear-text">{tc.existingTitle}</span>
                            </div>
                            {tc.area && (
                              <span className="text-xs px-2 py-0.5 rounded bg-linear-bg-card border border-linear-border text-linear-text-secondary mr-2">{tc.area}</span>
                            )}
                            {tc.relatedTickets.map(t => (
                              <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-linear-accent/20 text-linear-accent font-medium mr-1">{t}</span>
                            ))}
                            <p className="text-sm text-linear-text-muted mt-2">{tc.reason}</p>
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold flex-shrink-0 ml-4 ${badge.classes}`}>
                            {badge.label} Confidence
                          </span>
                        </div>
                      </div>
                    )
                  })
                )}
              </>
            )}
          </div>
        </div>
      </AppLayout>
    )
  }

  // ==========================================================================
  // Render: Input Screen
  // ==========================================================================
  return (
    <AppLayout
      breadcrumbs={[
        { label: 'Test Case Updater' },
      ]}
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-linear-text mb-2">Test Case Updater</h1>
          <p className="text-linear-text-muted">
            Upload your test suite and sprint tickets to find which test cases need updating, what gaps exist, and which cases are obsolete.
          </p>
        </div>

        {error && (
          <div className="toast toast-error mb-4">
            <svg className="w-5 h-5 text-error flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-linear-text">{renderAnalysisError(error)}</span>
          </div>
        )}

        {/* ================================================================ */}
        {/* Card A: Upload Test Suite CSV                                    */}
        {/* ================================================================ */}
        <div className={`card mb-6 transition-opacity ${analysisStatus === 'processing' ? 'opacity-60 pointer-events-none' : ''}`} aria-disabled={analysisStatus === 'processing'}>
          <h2 className="text-xl font-semibold text-linear-text mb-2">Upload Test Suite CSV</h2>
          <p className="text-linear-text-muted mb-6">
            Upload your test case library from TestRail or Zephyr Scale.
          </p>

          {/* File Upload Zone */}
          {!uploadedInfo ? (
            <div
              className={`upload-zone ${dragActive ? 'drag-active' : ''}`}
              onDragOver={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setDragActive(true)
              }}
              onDragEnter={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setDragActive(true)
              }}
              onDragLeave={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  setDragActive(false)
                }
              }}
              onDrop={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setDragActive(false)
                const file = e.dataTransfer.files?.[0]
                if (file) handleCsvFile(file)
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleCsvFile(file)
                }}
                className="hidden"
                id="csv-upload-tcu"
              />
              <label htmlFor="csv-upload-tcu" className="cursor-pointer block">
                <svg className="w-12 h-12 text-linear-text-muted mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <div className="text-linear-text font-medium mb-1">Click or drag to upload CSV file</div>
                <div className="text-linear-text-muted text-sm">Supports TestRail and Zephyr Scale formats</div>
              </label>
            </div>
          ) : (
            /* File info display after upload */
            <div className="bg-linear-bg-hover border border-linear-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-linear-accent/10 flex items-center justify-center">
                    <svg className="w-5 h-5 text-linear-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-linear-text">{uploadedInfo.fileName}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                        uploadedInfo.detectedFormat === 'testrail'
                          ? 'bg-info/15 text-info border border-info/30'
                          : uploadedInfo.detectedFormat === 'zephyr'
                            ? 'bg-warning/15 text-warning border border-warning/30'
                            : 'bg-linear-text-muted/15 text-linear-text-muted border border-linear-text-muted/30'
                      }`}>
                        {uploadedInfo.detectedFormat === 'testrail' ? 'TestRail' : uploadedInfo.detectedFormat === 'zephyr' ? 'Zephyr' : 'CSV'}
                      </span>
                      <span className="text-sm text-linear-text-muted">
                        {uploadedInfo.totalCases} test case{uploadedInfo.totalCases !== 1 ? 's' : ''}
                      </span>
                      {uploadedInfo.areas.length > 0 && (
                        <span className="text-sm text-linear-text-muted">
                          across {uploadedInfo.areas.length} area{uploadedInfo.areas.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={clearCsv}
                  disabled={analysisStatus === 'processing'}
                  className="btn btn-ghost text-linear-text-muted hover:text-error"
                  title="Remove file"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ================================================================ */}
        {/* Card B: Sprint Tickets                                          */}
        {/* ================================================================ */}
        <div className={`card mb-6 transition-opacity ${analysisStatus === 'processing' ? 'opacity-60 pointer-events-none' : ''}`} aria-disabled={analysisStatus === 'processing'}>
          {/* Header row with title and Sprint Import button */}
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-semibold text-linear-text">Sprint Tickets</h2>
            {jiraStatus?.status === 'active' && (
              <div className="flex items-center gap-2">
                {tickets.length >= 2 && (
                  <button
                    onClick={() => setTickets([])}
                    disabled={analysisStatus === 'processing'}
                    className="btn btn-ghost text-sm text-linear-text-muted"
                    title="Clear all tickets"
                  >
                    Clear All
                  </button>
                )}
                <button
                  onClick={() => setSprintModalOpen(true)}
                  disabled={analysisStatus === 'processing'}
                  className="btn btn-outline text-sm"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Import from Sprint
                </button>
              </div>
            )}
          </div>
          <p className="text-linear-text-muted mb-6 flex items-center gap-2">
            <span>Type ticket IDs and press Space, Comma, or Enter to add (e.g. BAT-100)</span>
            <span
              className="inline-flex items-center justify-center w-4 h-4 rounded-full text-xs text-linear-text-muted border border-linear-border cursor-help"
              title="If none of your tickets resolve, check that Jira is connected in Settings → Integrations."
              aria-label="If no tickets resolve, check that Jira is connected in Settings."
            >
              ?
            </span>
          </p>

          {/* Pills/Tags Container */}
          <div className="bg-linear-bg-input border border-linear-border-input rounded-lg p-3 mb-6 focus-within:border-linear-accent-focus transition-colors">
            <div className="flex flex-wrap items-center gap-2">
              {tickets.map((ticket) => (
                <div
                  key={ticket}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-linear-accent/20 text-linear-accent rounded-full text-sm font-medium flex-shrink-0"
                >
                  <span>{ticket}</span>
                  <button
                    onClick={() => removeTicket(ticket)}
                    disabled={analysisStatus === 'processing'}
                    className="hover:bg-linear-accent/30 rounded-full p-0.5 transition-colors"
                    type="button"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
              <input
                type="text"
                value={ticketInput}
                onChange={(e) => setTicketInput(e.target.value)}
                onKeyDown={handleTicketKeyDown}
                className="flex-1 min-w-[200px] bg-transparent border-none outline-none text-linear-text placeholder-linear-text-placeholder"
                placeholder={tickets.length === 0 ? "Type BAT-100 and press Space or Enter..." : ""}
                disabled={analysisStatus === 'processing'}
              />
            </div>
          </div>

          {/* JQL Import Section */}
          {jiraStatus?.status === 'active' && (
            <div className="mt-4 mb-6">
              <button
                onClick={() => setJqlExpanded(!jqlExpanded)}
                className="flex items-center gap-2 text-sm text-linear-text-muted hover:text-linear-text transition-colors"
              >
                <svg className={`w-4 h-4 transition-transform ${jqlExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                Advanced: Import from JQL
              </button>

              {jqlExpanded && (
                <div className="mt-3 p-4 bg-linear-bg-input border border-linear-border rounded-lg">
                  <textarea
                    value={jqlQuery}
                    onChange={e => setJqlQuery(e.target.value)}
                    placeholder='project = BAT AND fixVersion = "3.2"'
                    className="textarea font-mono text-sm w-full"
                    rows={3}
                  />
                  <div className="flex items-center gap-3 mt-3">
                    <button
                      onClick={handleJqlPreview}
                      disabled={!jqlQuery.trim() || jqlLoading}
                      className="btn btn-outline text-sm"
                    >
                      {jqlLoading ? 'Searching...' : 'Preview'}
                    </button>
                    {jqlError && <span className="text-sm text-error">{jqlError}</span>}
                    {jqlTotal > 0 && !jqlError && <span className="text-sm text-linear-text-muted">{jqlTotal} ticket{jqlTotal !== 1 ? 's' : ''} found</span>}
                  </div>

                  {/* JQL Results list with checkboxes */}
                  {jqlResults.length > 0 && (
                    <div className="mt-3 max-h-60 overflow-y-auto space-y-1">
                      <div className="flex items-center justify-between mb-2">
                        <label className="flex items-center gap-2 text-sm text-linear-text-muted cursor-pointer">
                          <input
                            type="checkbox"
                            checked={jqlSelectedKeys.size === jqlResults.length && jqlResults.length > 0}
                            onChange={toggleJqlSelectAll}
                            className="accent-linear-accent"
                          />
                          Select All
                        </label>
                        <button
                          onClick={handleJqlImport}
                          disabled={jqlSelectedKeys.size === 0}
                          className="btn btn-primary text-sm"
                        >
                          Import {jqlSelectedKeys.size} Ticket{jqlSelectedKeys.size !== 1 ? 's' : ''}
                        </button>
                      </div>
                      {jqlResults.map(issue => (
                        <label key={issue.key} className="flex items-center gap-3 p-2 rounded hover:bg-linear-bg-hover cursor-pointer">
                          <input
                            type="checkbox"
                            checked={jqlSelectedKeys.has(issue.key)}
                            onChange={() => toggleJqlKey(issue.key)}
                            className="accent-linear-accent flex-shrink-0"
                          />
                          <span className="font-mono text-sm text-linear-accent flex-shrink-0">{issue.key}</span>
                          <span className="text-sm text-linear-text truncate">{issue.summary}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${statusColor(issue.status.color)}`}>{issue.status.name}</span>
                        </label>
                      ))}
                      {jqlHasMore && (
                        <button
                          onClick={handleJqlLoadMore}
                          disabled={jqlLoading}
                          className="w-full text-center text-sm text-linear-accent hover:underline py-2"
                        >
                          {jqlLoading ? 'Loading...' : 'Load More'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Jira not connected hint */}
          {(!jiraStatus || jiraStatus.status === 'disconnected') && (
            <p className="text-xs text-linear-text-muted mt-2">
              Connect Jira in Settings to use Sprint Import and JQL search.
            </p>
          )}
        </div>

        {/* ================================================================ */}
        {/* Analyze Button / Progress Bar                                   */}
        {/* ================================================================ */}
        {analysisStatus === 'processing' ? (
          <div className="card p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="spinner spinner-sm"></div>
              <span className="text-linear-text font-medium">Analyzing test cases...</span>
            </div>
            <div className="progress-bar h-2 mb-2">
              <div
                className="progress-bar-fill transition-all duration-300"
                style={{ width: `${Math.max(analysisProgress, 5)}%` }}
              />
            </div>
            <p className="text-sm text-linear-text-muted">{analysisMessage || 'Starting analysis...'}</p>
          </div>
        ) : (
          <button
            onClick={handleStartAnalysis}
            disabled={loading || !csvFile || tickets.length === 0}
            className="btn btn-primary"
          >
            {loading ? (
              <>
                <div className="spinner spinner-sm"></div>
                Starting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                Analyze {tickets.length} ticket{tickets.length !== 1 ? 's' : ''} against {uploadedInfo?.totalCases || 0} test cases
              </>
            )}
          </button>
        )}
      </div>

      {/* ================================================================== */}
      {/* Sprint Import Modal                                               */}
      {/* ================================================================== */}
      {sprintModalOpen && (
        <SprintImportModal
          onClose={() => setSprintModalOpen(false)}
          onImport={(keys) => {
            const newTickets = [...new Set([...tickets, ...keys])]
            setTickets(newTickets)
            setSprintModalOpen(false)
          }}
          appMode="test-case-updater"
          jiraStatus={jiraStatus}
        />
      )}
    </AppLayout>
  )
}
